<template>
  <div
    class="p-[20px] md:p-[40px] bg-white flex flex-row content-center items-center md:items-start ml-0"
    id="breadcrumbs">
    <div class="flex" v-for="(crumb, idx) in breadcrumbs" :key="idx">
      <RouterLink
        v-if="crumb.clickable"
        :id="`breadcrumb-${idx}`"
        :data-test="`breadcrumb-${idx}`"
        class="mx-[10px] pb-[2px] flex"
        :class="'text-grey-2 border-b border-grey-2 hover:text-grey-1 capitalize'"
        :to="crumb.path">
        <HomeIcon
          v-if="idx === 0"
          class="flex self-center mr-[5px] size-4 stroke-black stroke-[1.5]" />
        {{ crumb.route }}
      </RouterLink>
      <span
        v-else
        :id="`breadcrumb-${idx}`"
        :data-test="`breadcrumb-${idx}`"
        class="mx-[10px] pb-[2px] flex font-bold text-grey-1 hover:text-primary">
        <HomeIcon
          v-if="idx === 0"
          class="flex self-center mr-[5px] size-4 stroke-black stroke-[1.5]" />
        {{ crumb.route }}
      </span>
      <span v-if="idx !== breadcrumbs.length - 1"> / </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useRouter, RouterLink } from 'vue-router'
import HomeIcon from '@/components/icons/HomeIcon.vue'

type Props = {
  currentPageName?: string
}
const route = useRoute()
const router = useRouter()
const props = withDefaults(defineProps<Props>(), {
  currentPageName: '',
})

const breadcrumbs = computed(() => {
  const crumbs = []

  // Always add home
  crumbs.push({
    route: 'home',
    path: '/',
    clickable: route.path !== '/',
  })

  // Get the first section after /
  const segments = route.path.split('/').filter(Boolean)
  let sectionName = ''
  if (segments.length > 1) {
    const sectionPath = '/' + segments[0]
    const resolved = router.resolve(sectionPath)
    sectionName = (resolved.name ?? segments[0]) as string
    crumbs.push({
      route: sectionName,
      path: sectionPath,
      clickable: route.path !== sectionPath,
    })
  }

  // Add current page as last breadcrumb (not clickable) only if different from section
  const lastName =
    props.currentPageName ||
    route.name ||
    (segments.length > 1 ? segments[segments.length - 1] : '')
  if (
    lastName &&
    lastName !== sectionName &&
    (!crumbs.length || crumbs[crumbs.length - 1].route !== lastName)
  ) {
    crumbs.push({
      route: lastName,
      path: route.fullPath,
      clickable: false,
    })
  }

  return crumbs
})
</script>
